import { css } from '@emotion/react'
import { colorTokens, spacingTokens } from '../../../styles/tokens'

/**
 * Emotion `css` objects for the full-bleed login shell.
 *
 * The codebase bans `sx`/`style` props outside `src/components/layout` and
 * `src/styles` (see `linter-plugins/no-inline-styles.grit`); the Emotion `css`
 * prop is the sanctioned escape hatch for layout-heavy sections.
 *
 * The look follows the marketing homepage (September 2026): Cabinet Grotesk at
 * 800 with tight tracking for headings, the ink and muted greys for copy, labels
 * above 44px inputs, pill buttons in the brand green, and a dark green panel on
 * the right with white 1.25rem cards on it. The split view itself is unchanged.
 */
const DISPLAY_FONT =
  "var(--font-cabinet-grotesk), 'system-ui', Arial, sans-serif"

export const styles = {
  shell: css`
    position: absolute;
    inset: 0;
    width: 100vw;
    height: 100vh;
    height: 100dvh;
    display: flex;
    background: ${colorTokens.white};
    outline: none;
    overflow: hidden;
  `,
  formColumn: css`
    flex: 1 1 100%;
    display: flex;
    flex-direction: column;
    position: relative;
    padding: ${spacingTokens.pageMargin}px;
    overflow-y: auto;

    @media (min-width: 768px) {
      flex: 1 1 50%;
      padding: ${spacingTokens.sectionGap}px;
    }
  `,
  brand: css`
    display: flex;
    align-items: center;
    gap: ${spacingTokens.cardGap}px;
    flex-wrap: wrap;
    /* Room for the close button, which sits over this row on one column. */
    padding-right: 36px;

    @media (min-width: 768px) {
      padding-right: 0;
    }
  `,
  // Shared heading treatment (Cabinet Grotesk, weight 800, tight tracking), the
  // homepage's display face. `!important` guarantees it wins over MUI's
  // Typography variant class regardless of style-injection order.
  headingFont: css`
    font-family: ${DISPLAY_FONT} !important;
    font-weight: 800 !important;
    letter-spacing: -0.025em !important;
  `,
  /** The form's title: the homepage's section-heading size, left-aligned. */
  heading: css`
    font-size: clamp(1.875rem, 3vw, 2.25rem) !important;
    line-height: 1.1 !important;
    color: ${colorTokens.slate900};
  `,
  /** The line under the title, in the homepage's muted grey. */
  lede: css`
    font-size: 1.0625rem;
    line-height: 1.6;
    color: ${colorTokens.slate600};
  `,
  /** A label above its input, as the enquiry form sets them. */
  label: css`
    display: block;
    font-size: 0.875rem;
    font-weight: 500;
    line-height: 1.4;
    color: ${colorTokens.slate900};
  `,
  /** A field: label over a 44px input with the hairline border and a 10px corner. */
  field: css`
    display: flex;
    flex-direction: column;
    gap: 6px;

    & .MuiInputBase-root {
      min-height: 44px;
      border-radius: 10px;
      font-size: 1rem;
      background: ${colorTokens.white};
    }
  `,
  /** The homepage's primary call to action: a 48px pill in the brand green. */
  primaryButton: css`
    min-height: 48px;
    padding: 0 24px;
    border-radius: 999px;
    font-size: 1rem;
    font-weight: 500;
    transition:
      background-color 300ms cubic-bezier(0.22, 1, 0.36, 1),
      scale 300ms cubic-bezier(0.22, 1, 0.36, 1);

    @media (prefers-reduced-motion: no-preference) {
      &:hover {
        scale: 1.02;
      }
      &:active {
        scale: 0.98;
      }
    }
  `,
  /** The secondary pill: white with the hairline, for "Back to sign in". */
  secondaryButton: css`
    min-height: 44px;
    padding: 0 20px;
    border-radius: 999px;
    border: 1px solid ${colorTokens.slate200};
    background: ${colorTokens.white};
    color: ${colorTokens.slate900};
    font-size: 1rem;
    font-weight: 500;

    &:hover {
      background: ${colorTokens.slate100};
    }
  `,
  /** An inline link, underlined so it reads as one without leaning on colour. */
  inlineLink: css`
    font-size: 0.875rem;
    font-weight: 500;
    color: ${colorTokens.slate900};
    text-decoration: underline;
    text-decoration-color: ${colorTokens.slate200};
    text-underline-offset: 4px;

    &:hover {
      text-decoration-color: ${colorTokens.slate900};
    }
  `,
  /** Supporting text beside a control, e.g. the "Remember me" label. */
  supporting: css`
    font-size: 0.875rem;
    color: ${colorTokens.slate900};
  `,
  /* Both marks fit beside the close button on a 360px phone at 108px; the full
     150px from the tablet width up. */
  logo: css`
    width: 108px;
    height: auto;

    @media (min-width: 768px) {
      width: 150px;
    }
  `,
  logoAiClaims: css`
    width: 108px;
    height: auto;

    @media (min-width: 768px) {
      width: 150px;
    }
  `,
  logoDivider: css`
    display: inline-block;
    width: 1px;
    height: 22px;
    background: ${colorTokens.slate300};
    flex-shrink: 0;
  `,
  closeButton: css`
    position: absolute;
    top: 16px;
    right: 16px;
    z-index: 2;
    color: ${colorTokens.slate700};

    @media (min-width: 768px) {
      color: ${colorTokens.white};
    }
  `,
  formBody: css`
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    width: 100%;
    max-width: 420px;
    margin: 0 auto;
    padding: ${spacingTokens.sectionGap}px 0;
  `,
  /** The homepage's one dark band, as the ground for the showcase cards. */
  showcaseColumn: css`
    display: none;

    @media (min-width: 768px) {
      display: flex;
      flex: 1 1 50%;
      position: relative;
      align-items: center;
      justify-content: center;
      /* No horizontal padding: the carousel viewport spans the full panel width
         (each slide carries its own padding) so content isn't clipped mid-panel
         during auto-scroll. */
      padding: 0;
      background-color: ${colorTokens.green900};
      overflow: hidden;

      /* A little brand green in the corner, as the hero's frame has behind it. */
      &::before {
        content: '';
        position: absolute;
        inset: 0;
        background: radial-gradient(
          circle at 18% 12%,
          rgba(67, 121, 67, 0.35),
          transparent 48%
        );
      }
    }
  `,
}
